import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/forms/button.component';
import Icon from '../../../../components/forms/icon.component';
import Input from '../../../../components/forms/input.component';
import { AlertService } from '../../../../services/alert.service';
import { FlowsService } from '../../../../services/flows.service';
import { AccountsService } from '../../../../services/accounts.service';
import Select from 'react-select';
import useModal from '../../../../hooks/use-close-modal.hook';
import { Controller, useForm } from 'react-hook-form';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import { CompaniesService } from '../../../../services/companies.service';

interface Option {
  value: string;
  label: string;
}

function AddFlowModal({ show, onRequestClose, onFlowCreated }: any) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
  const [accountOptions, setAccountOptions] = useState<Option[]>([]);

  const defaultValues = {
    name: '',
    company: '',
    account: ''
  };
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const selectedCompany = watch('company');

  const getCompanies = async () => {
    try {
      const res = await CompaniesService.getCompanies();
      setCompanyOptions(res.map((c: any) => ({ value: c._id, label: c.name })));
    } catch (err: any) {
      console.error(err);
      AlertService.toastError(err.error || err);
    }
  };

  const getAccountsForCompany = async (companyId: string) => {
    try {
      const res = await AccountsService.getAccountsForCompany(companyId);
      setAccountOptions(res.map((a: any) => ({ value: a._id, label: a.name })));
    } catch (err: any) {
      console.error(err);
      AlertService.toastError(err.error || err);
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const submit = async (data: any) => {
    if (!data.name || !data.account) {
      AlertService.toastError('Por favor, rellena todos los campos');
      return;
    }
    try {
      const created = await FlowsService.saveFlow({ name: data.name, account: data.account });
      AlertService.toastSuccess('Se ha creado el flujo');
      await closeModal();
      await sleep(800);
      await onFlowCreated(created);
    } catch (err: any) {
      console.log(err);
      AlertService.toastError(err.error || err);
    }
  };

  const closeModal = () => {
    onRequestClose(false);
    reset(defaultValues);
    setAccountOptions([]);
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => closeModal()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form onSubmit={handleSubmit(submit)}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">Crea un nuevo flujo</h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('channels.rcs.modal-C-Subtitle')
                    }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row">
                <div className="px-2">
                  <div className="fv-row mb-7">
                    <Input
                      label={'Nombre'}
                      requiredIndicator="required"
                      placeholder={'Nombre del flujo'}
                      errors={errors.name}
                      {...register('name', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>
                  <div className="fv-row mb-7">
                    <label className="form-label">Empresa</label>
                    <Controller
                      control={control}
                      name="company"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={companyOptions}
                            placeholder="Selecciona una empresa"
                            classNamePrefix="form-select-custom"
                            className={error ? 'is-invalid' : ''}
                            onChange={(opt: Option | null) => {
                              const companyId = opt ? opt.value : '';
                              onChange(companyId);
                              setValue('account', '');
                              if (companyId) getAccountsForCompany(companyId);
                            }}
                            value={companyOptions.find((o) => o.value === value) || null}
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                  {selectedCompany && (
                    <div className="fv-row mb-7">
                      <label className="form-label">Cuenta</label>
                      <Controller
                        control={control}
                        name="account"
                        rules={{ required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={accountOptions}
                              placeholder="Selecciona una cuenta"
                              classNamePrefix="form-select-custom"
                              className={error ? 'is-invalid' : ''}
                              onChange={(opt: Option | null) => onChange(opt ? opt.value : '')}
                              value={accountOptions.find((o) => o.value === value) || null}
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                  )}
                  <div className="d-flex flex-center flex-row-fluid pt-12">
                    <button
                      type="button"
                      className="btn btn-light hover-scale"
                      onClick={() => closeModal()}
                    >
                      {t('general.close')}
                    </button>
                    <Button
                      type="submit"
                      id="sendData"
                      className="btn btn-primary ms-3 hover-scale "
                      disabled={!isValid}
                      isLoading={isSubmitting}
                    >
                      Crear flujo
                      <Icon icon="ki-duotone ki-send ms-2 fs-1"></Icon>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddFlowModal;
